/* ============================================================================
   SG-2026 · Kontakt 2026 · Globales JavaScript
   ----------------------------------------------------------------------------
   Hauptkomponenten:

     1. <sg-ct26-status>    - "offen/geschlossen"-Chip + Event broadcast
     2. <sg-ct26-quiz>      - Multi-Step-Bedarfsklaerung (ersetzt Router)
     3. <sg-ct26-callback>  - Rueckruf-Tag/Zeitslot-Picker
     4. <sg-ct26-tracking>  - Carrier-Tracking (ohne Server-Call)

   Warum Quiz statt Router:
     > Wenn wir direkt 5 Kacheln zeigen und nur bei "Beratung" Telefon
       listen, sehen Kunden sofort "bei Service werde ich abgewimmelt".
     > Stattdessen fuehren wir in 2-3 Schritten durch. Der Kunde klickt
       auf "was trifft am ehesten zu?" - Antworten wirken wie normale
       Bedarfsklaerung. Telefon taucht nur dort auf, wo es wirklich
       sinnvoll ist (Beratung, B2B). Reklamations-/Technik-Pfade enden
       natuerlich auf E-Mail - ohne dass es sich wie Filterung anfuehlt.
   ============================================================================ */

(function () {
  'use strict';
  if (window.__sgContact2026Booted) return;
  window.__sgContact2026Booted = true;

  /* --------------------------------------------------------------------------
   * Berlin-Zeit Helper
   * ------------------------------------------------------------------------ */

  const WD_LONG  = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const WD_MAP   = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  function berlinNow() {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Berlin', hour12: false,
        weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      }).formatToParts(new Date());
      const m = {};
      parts.forEach(p => { if (p.type !== 'literal') m[p.type] = p.value; });
      let hour = parseInt(m.hour, 10);
      if (hour === 24) hour = 0;
      return {
        day:    WD_MAP[m.weekday] ?? new Date().getDay(),
        year:   parseInt(m.year, 10),
        month:  parseInt(m.month, 10),
        date:   parseInt(m.day, 10),
        hour:   hour,
        minute: parseInt(m.minute, 10)
      };
    } catch (_) {
      const d = new Date();
      return { day: d.getDay(), year: d.getFullYear(), month: d.getMonth()+1,
               date: d.getDate(), hour: d.getHours(), minute: d.getMinutes() };
    }
  }

  /* ==========================================================================
   * <sg-ct26-status>
   * Neutral formuliert: signalisiert Erreichbarkeit ohne Telefon zu erwaehnen.
   * ========================================================================== */

  class SgCt26Status extends HTMLElement {
    connectedCallback() {
      this.openHour  = parseInt(this.dataset.openHour  || '10', 10);
      this.closeHour = parseInt(this.dataset.closeHour || '16', 10);
      this.phone     = (this.dataset.phone || '').trim();
      this._textEl   = this.querySelector('.sg26-ct-hero__status-text');
      this._update();
      this._timer = setInterval(() => this._update(), 60 * 1000);
      this._onVis = () => { if (!document.hidden) this._update(); };
      document.addEventListener('visibilitychange', this._onVis);
    }
    disconnectedCallback() {
      if (this._timer) clearInterval(this._timer);
      if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
    }
    _update() {
      const now = berlinNow();
      const minNow = now.hour * 60 + now.minute;
      const isWeekday = now.day >= 1 && now.day <= 5;
      const isOpen = isWeekday && minNow >= this.openHour*60 && minNow < this.closeHour*60;
      this.dataset.state = isOpen ? 'open' : 'closed';

      if (this._textEl) {
        if (this.phone) {
          this._textEl.textContent = isOpen
            ? 'Jetzt telefonisch erreichbar · ' + this.phone
            : 'Telefonisch Mo–Fr ' + this.openHour + '–' + this.closeHour + ' Uhr · schriftlich jederzeit';
        } else {
          this._textEl.textContent = isOpen
            ? 'Wir sind heute erreichbar · Antwort werktags meist binnen 4 Stunden'
            : 'Schriftlich jederzeit möglich · wir antworten am nächsten Werktag';
        }
      }
      document.dispatchEvent(new CustomEvent('sg-ct26:status', { detail: { isOpen } }));
    }
  }
  if (!customElements.get('sg-ct26-status')) {
    customElements.define('sg-ct26-status', SgCt26Status);
  }

  /* ==========================================================================
   * QUIZ-FLOW-DEFINITION
   * --------------------------------------------------------------------------
   * Format:
   *   id: {
   *     question:   Text/HTML der Frage
   *     subtitle:   (optional) Erklaerung
   *     options: [  Array von Antwort-Optionen
   *       { label: "...", hint: "(optional)", next: "nextStepId" }
   *       { label: "...", resolve: "trackIntent" }                 // Endpunkt
   *     ]
   *   }
   *
   * Resolve-Werte mappen auf data-track der Sections:
   *   "beratung", "bestellung", "technik", "montage", "reklamation"
   *
   * Sprache bewusst gewaehlt:
   *   - Keine Kanal-Hints ("Telefon", "E-Mail")
   *   - Optionen klingen wie natuerliche Anliegen, nicht wie Filter
   *   - Immer in 2-3 Schritten zum Ziel
   * ======================================================================== */

  const FLOW = {
    root: {
      question: 'Wobei können wir Ihnen helfen?',
      options: [
        {
          label: 'Kaufberatung / Produktauswahl',
          hint:  'Welches Produkt passt zu Ihnen, Konfiguration, Angebot',
          resolve: 'beratung'
        },
        {
          label: 'Frage zu meiner Bestellung',
          hint:  'Versand, Rechnung, Unterlagen, Änderung',
          resolve: 'bestellung'
        },
        {
          label: 'Mein Produkt oder Gerät hat ein Problem',
          hint:  'Schaden, Defekt, Fehlfunktion, Widerruf',
          next:  'problem'
        },
        {
          label: 'Montage, Halterung oder Anmeldung',
          hint:  'Balkontyp, MaStR, Elektrik',
          resolve: 'montage'
        }
      ]
    },

    problem: {
      question: 'Was genau ist los?',
      subtitle: 'Wir dokumentieren alles schriftlich, damit wir Ihnen schnell und sauber helfen können.',
      options: [
        {
          label: 'Transportschaden oder beschädigt geliefert',
          hint:  'Am besten mit Fotos vom Karton und Produkt',
          resolve: 'reklamation'
        },
        {
          label: 'Falsches Produkt oder Teile fehlen',
          hint:  'Wir veranlassen Ersatz bzw. Nachlieferung',
          resolve: 'reklamation'
        },
        {
          label: 'Gerät funktioniert nicht wie erwartet',
          hint:  'LED-Code, App/WLAN, Leistung, Fehlermeldung',
          resolve: 'technik'
        },
        {
          label: 'Ich möchte zurücksenden (14-Tage-Widerruf)',
          resolve: 'reklamation'
        }
      ]
    }
  };

  /* ==========================================================================
   * <sg-ct26-quiz>
   * Multi-Step Bedarfsklaerung. Verwaltet Quiz-State + History.
   * Am Ende: setzt data-sg-contact-intent + scrollt zum Track.
   * ========================================================================== */

  const VALID_INTENTS = ['beratung', 'bestellung', 'technik', 'montage', 'reklamation'];

  class SgCt26Quiz extends HTMLElement {
    connectedCallback() {
      this._history   = [];
      this._currentId = null;
      this._loadingEl = this.querySelector('[data-sg-ct-quiz-loading]');

      // Deep-Link-Support: ?intent=beratung laedt direkt den Track
      const deeplink = this._readIntentFromUrl();
      if (deeplink) {
        this._resolve(deeplink, { skipScroll: true });
        return;
      }

      this._goto('root', { animated: false });
    }

    _readIntentFromUrl() {
      try {
        const url = new URL(window.location.href);
        let t = url.searchParams.get('intent') || url.searchParams.get('topic');
        if (!t && url.hash) {
          const m = url.hash.match(/(?:intent|topic)=([a-z0-9]+)/i);
          if (m) t = m[1];
        }
        if (t && VALID_INTENTS.indexOf(t.toLowerCase()) !== -1) return t.toLowerCase();
      } catch (_) {}
      return null;
    }

    _goto(stepId, opts = {}) {
      const step = FLOW[stepId];
      if (!step) return;

      // History
      if (this._currentId && !opts.isBack) this._history.push(this._currentId);
      this._currentId = stepId;

      this._render(step, opts.animated !== false);
    }

    _back() {
      if (this._history.length === 0) return;
      const prev = this._history.pop();
      this._currentId = null;
      this._goto(prev, { isBack: true, animated: true });
    }

    _resolve(intent, opts = {}) {
      if (VALID_INTENTS.indexOf(intent) === -1) return;

      document.documentElement.dataset.sgContactIntent = intent;
      document.querySelectorAll('.sg26-ct-track').forEach(trk => {
        if (trk.dataset.track === intent) trk.removeAttribute('hidden');
        else trk.setAttribute('hidden', '');
      });

      // URL aktualisieren (Deep-Link-Freundlichkeit)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('intent', intent);
        url.hash = '';
        window.history.replaceState({}, '', url);
      } catch (_) {}

      // Quiz auf "abgeschlossen" setzen und scrollen
      this._renderResolved(intent);

      if (!opts.skipScroll) {
        const target = document.querySelector('.sg26-ct-track[data-track="' + intent + '"]');
        if (target) {
          setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
        }
      }

      document.dispatchEvent(new CustomEvent('sg-ct26:intent', { detail: { intent } }));
    }

    _reset() {
      this._history = [];
      this._currentId = null;

      delete document.documentElement.dataset.sgContactIntent;
      document.querySelectorAll('.sg26-ct-track').forEach(trk => trk.setAttribute('hidden', ''));

      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('intent');
        url.searchParams.delete('topic');
        url.hash = '';
        window.history.replaceState({}, '', url);
      } catch (_) {}

      this._goto('root', { animated: true });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ---- Rendering ---- */

    _render(step, animated) {
      if (this._loadingEl) {
        this._loadingEl.remove();
        this._loadingEl = null;
      }

      const html = this._buildStepHtml(step);

      // WICHTIG: _wireCard MUSS nach dem finalen innerHTML-Set laufen,
      // sonst werden Events auf ein DOM gebunden, das gleich ueberschrieben wird.
      const apply = () => {
        this.innerHTML = html;
        this._wireCard();
        const card = this.querySelector('[data-sg-ct-quiz-card]');
        if (card && animated) {
          card.classList.add('is-entering');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => card.classList.remove('is-entering'));
          });
        }
      };

      const oldCard = this.querySelector('[data-sg-ct-quiz-card]');
      if (animated && oldCard) {
        oldCard.classList.add('is-leaving');
        // Pointer-Events off, damit Doppel-Klicks waehrend Animation nichts anrichten
        oldCard.style.pointerEvents = 'none';
        window.setTimeout(apply, 140);
      } else {
        apply();
      }
    }

    _buildStepHtml(step) {
      const depth = this._history.length + 1;
      const totalDots = Math.max(depth + 1, 3);
      let dots = '';
      for (let i = 0; i < totalDots; i++) {
        dots += '<span class="sg26-ct-quiz__progress-dot' + (i < depth ? ' is-active' : '') + '"></span>';
      }
      const breadcrumb =
        '<div class="sg26-ct-quiz__progress" aria-hidden="true">' +
          '<span class="sg26-ct-quiz__progress-label">Schritt ' + depth + '</span>' +
          '<div class="sg26-ct-quiz__progress-dots">' + dots + '</div>' +
        '</div>';

      const backBtn = this._history.length > 0
        ? '<button type="button" class="sg26-ct-quiz__back" data-sg-ct-quiz-back>' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>' +
            '<span>Zurück</span>' +
          '</button>'
        : '';

      const subtitle = step.subtitle
        ? '<p class="sg26-ct-quiz__subtitle">' + this._escape(step.subtitle) + '</p>'
        : '';

      const optionsHtml = step.options.map((opt, idx) => {
        const hint = opt.hint
          ? '<span class="sg26-ct-quiz__option-hint">' + this._escape(opt.hint) + '</span>'
          : '';
        const dataAttr = opt.next
          ? 'data-next="' + this._escape(opt.next) + '"'
          : 'data-resolve="' + this._escape(opt.resolve) + '"';
        return '<button type="button" class="sg26-ct-quiz__option" ' + dataAttr + ' data-idx="' + idx + '">' +
                 '<span class="sg26-ct-quiz__option-body">' +
                   '<span class="sg26-ct-quiz__option-label">' + this._escape(opt.label) + '</span>' +
                   hint +
                 '</span>' +
                 '<span class="sg26-ct-quiz__option-arrow" aria-hidden="true">' +
                   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>' +
                 '</span>' +
               '</button>';
      }).join('');

      return (
        '<div class="sg26-ct-quiz__card" data-sg-ct-quiz-card>' +
          breadcrumb +
          '<div class="sg26-ct-quiz__head">' +
            backBtn +
            '<h2 class="sg26-ct-quiz__question">' + this._escape(step.question) + '</h2>' +
            subtitle +
          '</div>' +
          '<div class="sg26-ct-quiz__options" role="list">' +
            optionsHtml +
          '</div>' +
        '</div>'
      );
    }

    _renderResolved(intent) {
      const labelMap = {
        beratung:    { title: 'Wir haben die passenden Optionen für Sie unten.',
                       lead:  'Persönliche Beratung für Ihr Anliegen.' },
        bestellung:  { title: 'Bestellinfos finden Sie direkt unten.',
                       lead:  'Die schnellsten Antworten stehen im nächsten Abschnitt.' },
        technik:     { title: 'Wir gehen das technische Anliegen gemeinsam an.',
                       lead:  'Kurze Checkliste + Ticket-Formular unten.' },
        montage:     { title: 'Hier sind die häufigsten Antworten zur Montage.',
                       lead:  'FAQ + Formular für offene Fragen unten.' },
        reklamation: { title: 'Wir kümmern uns umgehend — Formular unten.',
                       lead:  'Damit wir dokumentiert helfen können.' }
      };
      const cfg = labelMap[intent] || labelMap.beratung;

      const html =
        '<div class="sg26-ct-quiz__card sg26-ct-quiz__card--done" data-sg-ct-quiz-card>' +
          '<div class="sg26-ct-quiz__resolved">' +
            '<div class="sg26-ct-quiz__resolved-icon" aria-hidden="true">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                '<polyline points="20 6 9 17 4 12"/>' +
              '</svg>' +
            '</div>' +
            '<h2 class="sg26-ct-quiz__resolved-title">' + this._escape(cfg.title) + '</h2>' +
            '<p class="sg26-ct-quiz__resolved-lead">' + this._escape(cfg.lead) + '</p>' +
            '<button type="button" class="sg26-ct-quiz__restart" data-sg-ct-quiz-restart>' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                '<polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>' +
              '</svg>' +
              '<span>Anderes Anliegen prüfen</span>' +
            '</button>' +
          '</div>' +
        '</div>';

      const apply = () => {
        this.innerHTML = html;
        this._wireCard();
      };

      const oldCard = this.querySelector('[data-sg-ct-quiz-card]');
      if (oldCard) {
        oldCard.classList.add('is-leaving');
        oldCard.style.pointerEvents = 'none';
        window.setTimeout(apply, 140);
      } else {
        apply();
      }
    }

    _wireCard() {
      const options = this.querySelectorAll('.sg26-ct-quiz__option');
      options.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const next    = btn.getAttribute('data-next');
          const resolve = btn.getAttribute('data-resolve');
          if (next)         this._goto(next, { animated: true });
          else if (resolve) this._resolve(resolve);
        });
      });

      const back = this.querySelector('[data-sg-ct-quiz-back]');
      if (back) back.addEventListener('click', (e) => { e.preventDefault(); this._back(); });

      const restart = this.querySelector('[data-sg-ct-quiz-restart]');
      if (restart) restart.addEventListener('click', (e) => { e.preventDefault(); this._reset(); });
    }

    _escape(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  if (!customElements.get('sg-ct26-quiz')) {
    customElements.define('sg-ct26-quiz', SgCt26Quiz);
  }

  /* ==========================================================================
   * Beratung-Track: Call-Card je nach Oeffnungszeit primary/dimmed
   * ========================================================================== */

  function updateBeratungChannels() {
    const wrap = document.querySelector('[data-sg-ct-channels]');
    if (!wrap) return;

    const openHour  = parseInt(wrap.dataset.openHour  || '10', 10);
    const closeHour = parseInt(wrap.dataset.closeHour || '16', 10);
    const now = berlinNow();
    const isOpen = now.day >= 1 && now.day <= 5 &&
                   (now.hour * 60 + now.minute) >= openHour * 60 &&
                   (now.hour * 60 + now.minute) < closeHour * 60;

    const call     = wrap.querySelector('[data-channel="call"]');
    const callback = wrap.querySelector('[data-channel="callback"]');

      if (call) {
      call.classList.toggle('is-primary', isOpen);
      call.classList.toggle('is-disabled', !isOpen);
      const badge = call.querySelector('[data-sg-ct-channel-badge]');
      if (badge) {
        badge.textContent = isOpen
          ? 'Jetzt erreichbar'
          : 'Mo–Fr · ' + openHour + '–' + closeHour + ' Uhr';
      }
    }
    if (callback) callback.classList.toggle('is-primary', !isOpen);
  }

  document.addEventListener('DOMContentLoaded', updateBeratungChannels);
  document.addEventListener('sg-ct26:status', updateBeratungChannels);
  document.addEventListener('sg-ct26:intent', (e) => {
    if (e.detail.intent === 'beratung') setTimeout(updateBeratungChannels, 20);
  });

  /* ==========================================================================
   * <sg-ct26-callback>
   * Rueckruf-Picker: Tag → Zeitfenster → Formular
   * ========================================================================== */

  class SgCt26Callback extends HTMLElement {
    connectedCallback() {
      this.openHour  = parseInt(this.dataset.openHour  || '10', 10);
      this.closeHour = parseInt(this.dataset.closeHour || '16', 10);
      this.daysAhead = parseInt(this.dataset.daysAhead || '3', 10);
      this.slotLen   = 2; // Blockgroesse in Stunden

      this._daysWrap  = this.querySelector('.sg26-ct-callback__days');
      this._slotsWrap = this.querySelector('.sg26-ct-callback__slots');
      this._dayInput  = this.querySelector('[data-sg-ct-cb-day]');
      this._slotInput = this.querySelector('[data-sg-ct-cb-slot]');
      this._bodyInput = this.querySelector('[data-sg-ct-cb-body]');
      this._submit    = this.querySelector('button[type="submit"]');
      this._sumEmpty  = this.querySelector('[data-sg-ct-cb-summary-empty]');
      this._sumFilled = this.querySelector('[data-sg-ct-cb-summary-filled]');
      this._sumDay    = this.querySelector('[data-sg-ct-cb-summary-day]');
      this._sumSlot   = this.querySelector('[data-sg-ct-cb-summary-slot]');

      if (!this._daysWrap) return;
      this._renderDays();
    }

    _renderDays() {
      const now = berlinNow();
      const dayMatches = [];
      let cursor = new Date(Date.UTC(now.year, now.month - 1, now.date));
      const minNow = now.hour * 60 + now.minute;
      // Heute nur anbieten wenn mindestens noch ein kompletter Slot reinpasst
      const cutoffToday = this.closeHour * 60 - this.slotLen * 60;

      if (now.day >= 1 && now.day <= 5 && minNow < cutoffToday) {
        dayMatches.push({ date: new Date(cursor), label: 'Heute', wd: now.day });
      }
      let probe = new Date(cursor);
      while (dayMatches.length < this.daysAhead) {
        probe = new Date(probe.getTime() + 24 * 60 * 60 * 1000);
        const wd = probe.getUTCDay();
        if (wd >= 1 && wd <= 5) {
          const isMorgen = dayMatches.length === 0 ||
                           (dayMatches.length === 1 && dayMatches[0].label === 'Heute');
          dayMatches.push({
            date: probe,
            label: isMorgen ? 'Morgen' : WD_LONG[wd],
            wd: wd
          });
        }
      }

      this._daysWrap.innerHTML = '';
      dayMatches.forEach((d, idx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sg26-ct-callback__day';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', 'false');
        const dateStr = d.date.getUTCDate().toString().padStart(2, '0') + '.' +
                        (d.date.getUTCMonth() + 1).toString().padStart(2, '0');
        btn.innerHTML = '<span>' + d.label + '</span><span>' + dateStr + '</span>';
        btn.addEventListener('click', () => this._pickDay(btn, d));
        this._daysWrap.appendChild(btn);
        if (idx === 0) this._pickDay(btn, d);
      });
    }

    _pickDay(btn, dayInfo) {
      this._daysWrap.querySelectorAll('.sg26-ct-callback__day').forEach(b => {
        b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
      });
      this._activeDay = dayInfo;
      this._renderSlots(dayInfo);
      if (this._dayInput) {
        const dateStr = dayInfo.date.getUTCDate().toString().padStart(2, '0') + '.' +
                        (dayInfo.date.getUTCMonth() + 1).toString().padStart(2, '0') + '.' +
                        dayInfo.date.getUTCFullYear();
        this._dayInput.value = dayInfo.label + ' (' + dateStr + ')';
      }
      this._updateSummary();
    }

    _renderSlots(dayInfo) {
      if (!this._slotsWrap) return;
      this._slotsWrap.innerHTML = '';
      this._activeSlot = null;
      const now = berlinNow();
      const isToday = dayInfo.label === 'Heute';
      const minNow  = now.hour * 60 + now.minute;
      let firstSlot = null;

      for (let h = this.openHour; h + this.slotLen <= this.closeHour; h += this.slotLen) {
        // Heute: Slot muss mindestens 30 Min in der Zukunft starten
        if (isToday && h * 60 < minNow + 30) continue;
        const slot = document.createElement('button');
        slot.type = 'button';
        slot.className = 'sg26-ct-callback__slot';
        slot.setAttribute('role', 'radio');
        slot.setAttribute('aria-checked', 'false');
        slot.textContent = h + ':00 – ' + (h + this.slotLen) + ':00';
        slot.addEventListener('click', () => this._pickSlot(slot));
        this._slotsWrap.appendChild(slot);
        if (!firstSlot) firstSlot = slot;
      }
      if (this._slotsWrap.children.length === 0) {
        this._slotsWrap.innerHTML = '<p class="sg26-ct-callback__slots-empty" style="grid-column:1/-1">Heute ist kein Slot mehr frei — bitte einen anderen Tag wählen.</p>';
      } else if (firstSlot) {
        // Ersten Slot automatisch vorauswählen → Submit wird sofort aktiv
        this._pickSlot(firstSlot);
      }
    }

    _pickSlot(btn) {
      this._slotsWrap.querySelectorAll('.sg26-ct-callback__slot').forEach(s => {
        s.setAttribute('aria-checked', s === btn ? 'true' : 'false');
      });
      this._activeSlot = btn.textContent;
      if (this._slotInput) this._slotInput.value = this._activeSlot;
      this._updateSummary();
    }

    _updateSummary() {
      const hasAll = this._activeDay && this._activeSlot;
      if (this._sumEmpty)  this._sumEmpty.hidden  = !!hasAll;
      if (this._sumFilled) this._sumFilled.hidden = !hasAll;
      if (hasAll) {
        if (this._sumDay)  this._sumDay.textContent  = this._activeDay.label;
        if (this._sumSlot) this._sumSlot.textContent = this._activeSlot;

        /* Mitarbeiter-Info: Slot-Zusammenfassung als Body-Prefix vorausfüllen.
         * Überschreibt nur auto-generierte Prefixes (data-auto), nie vom
         * Kunden eingetippten Text. */
        if (this._bodyInput) {
          const prefix = '>>> Rückruf gewünscht: ' + this._activeDay.label + ' ' +
                         this._activeSlot + ' Uhr <<<\n\n';
          const cur = this._bodyInput.value;
          if (!cur || this._bodyInput.dataset.autoPrefix === 'true') {
            this._bodyInput.value = prefix;
            this._bodyInput.dataset.autoPrefix = 'true';
          } else if (!cur.startsWith('>>>')) {
            // Nutzer hat eigenen Text: Prefix voranstellen ohne zu überschreiben
            this._bodyInput.value = prefix + cur.replace(/^>>>[^\n]*\n\n/, '');
            this._bodyInput.dataset.autoPrefix = 'true';
          } else {
            // Bereits ein Prefix → aktualisieren
            this._bodyInput.value = prefix + cur.replace(/^>>>[^\n]*\n\n/, '');
          }
        }
      }
      if (this._submit) this._submit.disabled = !hasAll;
    }
  }
  if (!customElements.get('sg-ct26-callback')) {
    customElements.define('sg-ct26-callback', SgCt26Callback);
  }

  /* ==========================================================================
   * <sg-ct26-tracking>  (Carrier-Tracking)
   * ========================================================================== */

  const CARRIER_URLS = {
    dhl:    (nr) => 'https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=' + encodeURIComponent(nr),
    dpd:    (nr) => 'https://tracking.dpd.de/status/de_DE/parcel/' + encodeURIComponent(nr),
    hermes: (nr) => 'https://www.myhermes.de/empfangen/sendungsverfolgung/sendungsinformation/#' + encodeURIComponent(nr),
    gls:    (nr) => 'https://gls-group.eu/DE/de/paketverfolgung?match=' + encodeURIComponent(nr),
    ups:    (nr) => 'https://www.ups.com/track?tracknum=' + encodeURIComponent(nr)
  };

  class SgCt26Tracking extends HTMLElement {
    connectedCallback() {
      this._input   = this.querySelector('[data-sg-ct-tracking-input]');
      this._carrier = this.querySelector('[data-sg-ct-tracking-carrier]');
      this._go      = this.querySelector('[data-sg-ct-tracking-go]');
      this._hint    = this.querySelector('[data-sg-ct-tracking-hint]');

      if (!this._input || !this._go || !this._carrier) return;
      this._go.addEventListener('click', () => this._submit());
      this._input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this._submit(); }
      });
      this._carrier.addEventListener('change', () => this._syncCarrierState());
      this._syncCarrierState();
    }
    _syncCarrierState() {
      const isSpedition = this._carrier.value === 'spedition';
      if (this._go) this._go.textContent = isSpedition ? 'Speditionsstatus anfragen →' : 'Paket verfolgen →';
      if (this._input) {
        this._input.placeholder = isSpedition
          ? 'z. B. Bestellnummer, Referenz oder Lieferschein-Nr.'
          : 'z. B. 0529… oder JJD000…';
      }
      if (this._hint) {
        this._hint.textContent = isSpedition
          ? 'Wir öffnen die Statusanfrage und übernehmen Ihre Nummer direkt ins Formular.'
          : 'Paketdienst ausgewählt. Bitte Sendungsnummer eingeben und verfolgen.';
      }
    }
    _openSpeditionForm(raw) {
      const formWrap = document.querySelector('[data-sg-ct-bestellung-form-wrap]');
      if (formWrap) {
        formWrap.setAttribute('open', '');
        formWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      const anliegen = document.querySelector('[data-sg-ct-bestellung-anliegen]');
      if (anliegen) anliegen.value = 'Speditionslieferung (Statusanfrage)';

      if (raw) {
        const body = document.querySelector('[data-sg-ct-bestellung-body]');
        if (body && !body.value.trim()) {
          body.value = 'Spedition-Referenz: ' + raw + '\n\nBitte Status prüfen.';
        }
      }
    }
    _submit() {
      const raw = (this._input.value || '').trim();
      const carrierId = this._carrier.value;

      if (carrierId === 'spedition') {
        this._openSpeditionForm(raw);
        if (this._hint) this._hint.textContent = 'Speditionsanfrage geöffnet. Bitte ergänzen und absenden.';
        return;
      }

      if (!raw) { this._input.focus(); this._hint.textContent = 'Bitte zuerst eine Tracking-Nummer eingeben.'; return; }
      const clean = raw.replace(/\s+/g, '');
      const urlFn = CARRIER_URLS[carrierId];
      if (!urlFn) { this._hint.textContent = 'Paketdienst nicht unterstützt.'; return; }
      window.open(urlFn(clean), '_blank', 'noopener,noreferrer');
    }
  }
  if (!customElements.get('sg-ct26-tracking')) {
    customElements.define('sg-ct26-tracking', SgCt26Tracking);
  }

  /* ==========================================================================
   * Technik-Checkliste → Hidden-Felder syncen
   * ========================================================================== */

  document.addEventListener('DOMContentLoaded', () => {
    const boxes  = document.querySelectorAll('[data-sg-ct-check]');
    const fields = document.querySelectorAll('[data-sg-ct-check-field]');
    if (!boxes.length || !fields.length) return;
    const fieldMap = {};
    fields.forEach(f => { fieldMap[f.dataset.sgCtCheckField] = f; });
    boxes.forEach(box => {
      box.addEventListener('change', () => {
        const key = box.dataset.sgCtCheck;
        if (fieldMap[key]) fieldMap[key].value = box.checked ? 'ja' : 'nein';
      });
    });
  });

  /* ==========================================================================
   * Smooth-Scroll fuer Track-interne Anker (Callback, Form)
   * ========================================================================== */

  document.addEventListener('click', (e) => {
    const link = e.target.closest('.sg26-ct-channel[href^="#"]');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

})();
